import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const STORAGE_KEY = "procurement.projectFilter";

function readSaved() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || "all";
  } catch {
    return "all";
  }
}

/**
 * One project filter shared by every procurement page (the choice is remembered when you move
 * between pages). `apply(rows, getProjectIds)` keeps the rows of the chosen project;
 * `getProjectIds(row)` may return one id, a list of ids, or null.
 */
export function useProjectFilter() {
  const [value, setValue] = useState(readSaved);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
  }, []);

  const change = (next) => {
    setValue(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode — just don't remember */
    }
  };

  const apply = (rows, getProjectIds) => {
    if (!Array.isArray(rows) || value === "all") return rows || [];
    return rows.filter((row) => {
      const raw = getProjectIds(row);
      const ids = Array.isArray(raw) ? raw.filter(Boolean) : raw ? [raw] : [];
      return value === "none" ? ids.length === 0 : ids.includes(value);
    });
  };

  return { value, change, projects, apply, active: value !== "all" };
}

export function ProjectFilter({ filter }) {
  return (
    <Select value={filter.value} onValueChange={filter.change}>
      <SelectTrigger className={`h-9 w-56 [&>span]:truncate ${filter.active ? "border-amber-500/60" : ""}`} data-testid="project-filter-select">
        <SelectValue placeholder="All projects" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All projects</SelectItem>
        <SelectItem value="none">Not linked to a project</SelectItem>
        {filter.projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
